// src/pages/Newsletter.tsx

import React, { useState, useContext, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Button } from '@mui/material';
import { NotificationContext } from '../../contexts/NotificationContext'; // Import NotificationContext

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animated components with delay props (will be defined after base components)
let AnimatedNewsletterContainer: any;
let AnimatedTitle: any;
let AnimatedDescription: any;
let AnimatedForm: any;

const NewsletterContainer = styled.div`
  background-color: var(--color-section-4-bg);
  padding: 3rem 2rem;
  text-align: center;
  margin-top: 2rem;
`;

const Title = styled.h2`
  color: var(--color-text);
  font-size: 2rem;
  margin-bottom: 1rem;
  font-family: var(--font-primary);
`;

const Description = styled.p`
  color: var(--color-text);
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  justify-content: center;
  gap: 1rem;
  max-width: 500px;
  margin: 0 auto;
  user-select: none;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Input = styled.input`
  padding: 0.5rem 1rem;
  font-size: 1rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  flex-grow: 1;
  width: 100%;
`;


// Define animated components after base components
AnimatedNewsletterContainer = styled(NewsletterContainer)<{ isVisible: boolean }>`
  animation: ${({ isVisible }) => isVisible ? fadeInUp : 'none'} 0.8s ease-out forwards;
  opacity: 0;
`;

AnimatedTitle = styled(Title)<{ delay?: number; isVisible: boolean }>`
  animation: ${({ isVisible }) => isVisible ? fadeInUp : 'none'} 0.8s ease-out forwards;
  animation-delay: ${({ delay }) => delay || 0}s;
  opacity: 0;
`;

AnimatedDescription = styled(Description)<{ delay?: number; isVisible: boolean }>`
  animation: ${({ isVisible }) => isVisible ? fadeInUp : 'none'} 0.8s ease-out forwards;
  animation-delay: ${({ delay }) => delay || 0}s;
  opacity: 0;
`;

AnimatedForm = styled(Form)<{ delay?: number; isVisible: boolean }>`
  animation: ${({ isVisible }) => isVisible ? fadeInUp : 'none'} 0.8s ease-out forwards;
  animation-delay: ${({ delay }) => delay || 0}s;
  opacity: 0;
`;

const Newsletter: React.FC = () => {
  const newsletterRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const { showNotification } = useContext(NotificationContext); // Access Notification Context
  const [email, setEmail] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const currentRef = newsletterRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasAnimated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const response = await fetch('https://riskmaster.pro/add_newsletter_subscriber_proxy.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        showNotification('Thank you for subscribing! Please check your email to confirm your subscription.');
        setEmail(''); // Clear the input field
      } else if (data.message && data.message.includes('already subscribed')) {
        showNotification('You are already subscribed to our newsletter.');
      } else {
        showNotification(data.message || 'An error occurred. Please try again later.');
      }
    } catch (error) {
      showNotification('Unable to subscribe. Please try again later.');
      console.error('Newsletter Subscription Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatedNewsletterContainer isVisible={isVisible} ref={newsletterRef}>
      <AnimatedTitle isVisible={isVisible} delay={0.2}>Stay Updated</AnimatedTitle>
      <AnimatedDescription isVisible={isVisible} delay={0.4}>Subscribe to our newsletter for the latest updates and tips.</AnimatedDescription>
      <AnimatedForm isVisible={isVisible} delay={0.6} onSubmit={handleSubmit}>
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Button 
          variant="contained" 
          type="submit" 
          disabled={isSubmitting}
          sx={{ 
            width: { xs: '100%', sm: '150px' },
            height: '42px'
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Subscribe'}
        </Button>
      </AnimatedForm>
    </AnimatedNewsletterContainer>
  );
};

export default Newsletter;
